// PHASE_1 / B3 — the org-snapshots Content component (ADR-014 §2).
// Faz 8.6 / Batch A5 restyles the VISUAL layer to the novem language (serif
// PageHeader+em, a StatCard strip, a Card-wrapped keyset table). The DATA layer
// is untouched: the SAME read-only useKeysetQuery over GET /v1/org/snapshots
// (keyset, newest first). The scheduled daily-capture job is deferred (P4/P5
// carryover); this surface reads existing snapshots only. No money.
//
// captured_at renders through the shared UTC-pinned formatDate (the snapshot is
// "the day's capture" — shown as a calendar date, zone-independent, matching
// the A2 date-cell convention) instead of a raw .slice(0,10).

import { useTranslation } from "react-i18next";

import {
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  StatCard,
  type DataTableColumn,
} from "@novemai/components";
import { CalendarClock, History } from "lucide-react";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { formatDate } from "@/lib/date-range";
import { formatCountWithMore } from "@/lib/count";
import { fetchSnapshotsPage, type OrgSnapshotRow } from "./api";

export function OrgSnapshotsContent(): React.ReactElement {
  const { t, i18n } = useTranslation();

  const query = useKeysetQuery<OrgSnapshotRow>({
    queryKey: ["org", "snapshots"],
    fetchPage: fetchSnapshotsPage,
  });

  // Newest first (keyset order) → the head row is the latest capture date.
  const latest = query.items[0]?.captured_at ?? null;

  const columns: readonly DataTableColumn<OrgSnapshotRow>[] = [
    {
      id: "captured",
      header: t("org.snapshots_column_captured"),
      cell: (row): React.ReactNode => {
        const shown: string = row.captured_at
          ? formatDate(i18n.language, row.captured_at)
          : t("org.snapshots_no_date");
        return (
          <span className="font-serif text-[14.5px] text-[var(--fg-primary)]">{shown}</span>
        );
      },
    },
    {
      id: "kind",
      header: t("org.snapshots_column_kind"),
      cell: (row): string => t(`org.snapshot_kind.${row.snapshot_kind}`),
    },
    {
      id: "count",
      header: t("org.snapshots_column_count"),
      align: "end",
      cell: (row): string => String(row.employee_count),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              label={t("org.snapshots_kpi_total")}
              value={formatCountWithMore(query.items.length, query.hasNextPage)}
              tone="accent"
              icon={<History size={18} />}
            />
            <StatCard
              label={t("org.snapshots_kpi_latest")}
              value={latest ? formatDate(i18n.language, latest) : t("org.snapshots_no_date")}
              tone="info"
              icon={<CalendarClock size={18} />}
            />
          </div>

          <Card className="overflow-hidden">
            <DataTable<OrgSnapshotRow>
              caption={t("org.snapshots_title")}
              columns={columns}
              rows={query.items}
              rowKey={(row): string => row.snapshot_id}
              hasNextPage={query.hasNextPage}
              onLoadMore={query.fetchNextPage}
              isFetchingNextPage={query.isFetchingNextPage}
              loadMoreLabel={t("org.load_more")}
            />
          </Card>
        </>
      ) : (
        <Empty
          title={t("org.snapshots_empty_title")}
          description={t("org.snapshots_empty_description")}
        />
      )}
    </div>
  );
}
